import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { TbMenu2, TbX } from 'react-icons/tb';
import { useTheme } from '@contexts';
import { LogoIcon } from '../../atoms';

const StyledNavbar = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  
  background-color: ${({ $scrolled, theme }) =>
    $scrolled
      ? 'rgba(15, 20, 25, 0.9)'
      : 'transparent'
  };
  
  backdrop-filter: ${({ $scrolled }) => $scrolled ? 'blur(16px)' : 'none'};
  -webkit-backdrop-filter: ${({ $scrolled }) => $scrolled ? 'blur(16px)' : 'none'};
  
  border-bottom: ${({ $scrolled, theme }) =>
    $scrolled ? `1px solid ${theme.colors.border.light}` : 'none'
  };
  
  transition: all 0.3s ease;
  height: 72px;
`;

const NavbarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 3rem;
  height: 100%;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  
  @media (max-width: 1024px) {
    padding: 0 2rem;
  }
  
  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const Logo = styled(RouterLink)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: 1.25rem;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: none;
  z-index: 10;
`;

const LogoIconWrapper = styled.span`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavLinks = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 2.5rem;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavLink = styled(RouterLink)`
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: 0.9375rem;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: none;
  transition: color 0.2s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary.main};
  }
`;

const NavbarControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  z-index: 10;
`;

const CtaButton = styled(motion.button)`
  background-color: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.background.default};
  border: none;
  border-radius: 9999px;
  padding: 0.625rem 1.25rem;
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: 0.875rem;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary.main};
    color: white;
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.primary};
  padding: 0.5rem;
  
  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 72px;
  left: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colors.background.paper};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  padding: 1.5rem;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MobileNavLink = styled(RouterLink)`
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: 1rem;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: none;
  padding: 0.5rem 0;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary.main};
  }
`;

const MobileCtaButton = styled(RouterLink)`
  background-color: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.background.default};
  border: none;
  border-radius: 6px;
  padding: 0.75rem;
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: 1rem;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  text-align: center;
  text-decoration: none;
  margin-top: 0.5rem;
`;

const navLinks = [
  { path: '/', label: 'Home' },
  { path: '/product', label: 'Product' },
  { path: '/about', label: 'About' },
  { path: '/team', label: 'Team' },
  { path: '/contact', label: 'Contact' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <StyledNavbar $scrolled={scrolled}>
      <NavbarContainer>
        <Logo to="/">
          <LogoIconWrapper>
            <LogoIcon />
          </LogoIconWrapper>
          <span>SenseAI</span>
        </Logo>

        <NavLinks>
          {navLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
            >
              {link.label}
            </NavLink>
          ))}
        </NavLinks>

        <NavbarControls>
          <CtaButton whileTap={{ scale: 0.95 }}>
            Get Started
          </CtaButton>
          <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
            {mobileMenuOpen ? <TbX size={24} /> : <TbMenu2 size={24} />}
          </MobileMenuButton>
        </NavbarControls>
      </NavbarContainer>

      <AnimatePresence>
        {mobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            {navLinks.map((link) => (
              <MobileNavLink
                key={link.path}
                to={link.path}
                onClick={closeMobileMenu}
              >
                {link.label}
              </MobileNavLink>
            ))}
            <MobileCtaButton to="/contact" onClick={closeMobileMenu}>
              Get Started
            </MobileCtaButton>
          </MobileMenu>
        )}
      </AnimatePresence>
    </StyledNavbar>
  );
};

export default Navbar;
